import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { MenuProps, MenuItem } from './types';
import { MenuItemComponent } from './MenuItem';
import { useOutsideClick } from './useOutsideClick';
import { useMenuKeyboard } from './useMenuKeyboard';

export const Menu = React.memo(function Menu({
    items,
    isOpen,
    onClose,
    ariaLabel,
    triggerRef,
    className,
    layout,
    gridColumns,
    isSubmenu,
    onSubmenuClose,
}: MenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    const openSubmenuForItem = useCallback((itemId: string | null) => {
        setOpenSubmenuId(itemId);
    }, []);

    const handleKeyDown = useMenuKeyboard({
        menuRef,
        onClose,
        triggerRef,
        isSubmenu,
        onSubmenuClose,
        openSubmenuForItem,
    });

    // Build refs array for outside click
    const outsideClickRefs = useMemo(() => {
        const refs: React.RefObject<HTMLElement | null>[] = [menuRef as React.RefObject<HTMLElement | null>];
        if (triggerRef) refs.push(triggerRef);
        return refs;
    }, [triggerRef]);

    useOutsideClick(outsideClickRefs, isOpen, onClose);

    // Focus first non-disabled item on open
    useEffect(() => {
        if (!isOpen || !menuRef.current) return;
        // Small delay to let the DOM render
        const timer = requestAnimationFrame(() => {
            if (!menuRef.current) return;
            const allItems = menuRef.current.querySelectorAll<HTMLElement>('[data-menu-item]');
            // Only direct items
            const directItems = Array.from(allItems).filter(
                (el) => el.closest('[role="menu"]') === menuRef.current,
            );
            const firstEnabled = directItems.find(
                (el) => el.getAttribute('aria-disabled') !== 'true',
            );
            if (firstEnabled) {
                // Set roving tabindex
                for (const el of directItems) {
                    el.setAttribute('tabindex', '-1');
                }
                firstEnabled.setAttribute('tabindex', '0');
                firstEnabled.focus();
            }
        });
        return () => cancelAnimationFrame(timer);
    }, [isOpen]);

    // Reset submenu state when menu closes
    useEffect(() => {
        if (!isOpen) {
            setOpenSubmenuId(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const classNames = [
        'kollab-menu',
        isSubmenu ? 'kollab-menu--submenu' : '',
        layout === 'grid' ? 'kollab-menu--grid' : '',
        className || '',
    ]
        .filter(Boolean)
        .join(' ');

    const style: React.CSSProperties = {};
    if (layout === 'grid' && gridColumns) {
        style.gridTemplateColumns = `repeat(${gridColumns}, 1fr)`;
    }

    return (
        <div
            ref={menuRef}
            role="menu"
            aria-label={ariaLabel}
            className={classNames}
            style={style}
            onKeyDown={handleKeyDown}
        >
            {items.map((item) => {
                if (item.type === 'separator') {
                    return (
                        <div
                            key={item.id}
                            className="kollab-menu-separator"
                            role="separator"
                        />
                    );
                }

                if (item.type === 'label') {
                    return (
                        <div
                            key={item.id}
                            className="kollab-menu-label"
                            role="presentation"
                        >
                            {item.text}
                        </div>
                    );
                }

                if (item.type === 'submenu') {
                    const isSubmenuOpen = openSubmenuId === item.id;
                    return (
                        <div key={item.id} style={{ position: 'relative' }}>
                            <MenuItemComponent
                                item={item}
                                focused={false}
                                tabIndex={-1}
                                onClick={() => {
                                    setOpenSubmenuId(isSubmenuOpen ? null : item.id);
                                }}
                                hasOpenSubmenu={isSubmenuOpen}
                            />
                            <Menu
                                items={item.children}
                                isOpen={isSubmenuOpen}
                                onClose={() => setOpenSubmenuId(null)}
                                ariaLabel={typeof item.label === 'string' ? item.label : 'Submenu'}
                                isSubmenu={true}
                                onSubmenuClose={() => {
                                    setOpenSubmenuId(null);
                                    // Return focus to the submenu trigger
                                    const trigger = menuRef.current?.querySelector<HTMLElement>(
                                        `[data-menu-item-id="${item.id}"]`,
                                    );
                                    trigger?.focus();
                                }}
                                layout={item.layout}
                                gridColumns={item.gridColumns}
                            />
                        </div>
                    );
                }

                return (
                    <MenuItemComponent
                        key={item.id}
                        item={item}
                        focused={false}
                        tabIndex={-1}
                        onClick={() => {
                            if ('onClick' in item && item.onClick) {
                                item.onClick();
                            }
                        }}
                    />
                );
            })}
        </div>
    );
});
